/**
 * useProductUpdateNotification — emit a "What's new" notification on next login
 * when the app's product version is newer than the user's last-seen version.
 *
 * Reuses the onboarding last-seen-version store (public.user_onboarding) rather
 * than inventing a parallel one: the foundational tour already tracks
 * (user_id, tour_id, version) there. We use tour_id = 'product_update' and
 * version = CURRENT_PRODUCT_VERSION. A terminal-status row at the current
 * version means the user has already been notified.
 *
 * Flow on mount (once per session, gated on auth + workspace):
 *   1. Look up user_onboarding for (user, 'product_update', CURRENT_PRODUCT_VERSION).
 *   2. If a terminal row exists -> already notified, do nothing.
 *   3. Otherwise call notify_product_update (idempotent server-side per
 *      user+version), then upsert the user_onboarding row to mark it seen.
 *
 * Both the RPC and the marker write are idempotent, so a race (e.g. two tabs)
 * cannot double-notify: the RPC dedupes on payload->>'version', and the marker
 * upserts on the (user_id, tour_id, version) unique constraint.
 *
 * Mount once, in DashboardLayout, after auth + workspace providers.
 */

import { useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useWorkspaces } from '@/contexts/WorkspaceContext';
import { captureError } from '@/lib/sentry';
import {
  CURRENT_PRODUCT_VERSION,
  PRODUCT_UPDATE_BODY,
  PRODUCT_UPDATE_TITLE,
  PRODUCT_UPDATE_TOUR_ID,
} from '@/lib/product-update';

const TERMINAL_STATUSES = ['completed', 'dismissed_permanent'];

export function useProductUpdateNotification(): void {
  const { user, loading: authLoading } = useAuth();
  const { currentWorkspace } = useWorkspaces();
  // Only attempt once per mount, even if auth/workspace state flickers.
  const checkedRef = useRef(false);

  const userId = user?.id;
  const wsId = currentWorkspace?.id;

  useEffect(() => {
    if (authLoading) return;
    if (!userId || !wsId) return;
    if (checkedRef.current) return;
    checkedRef.current = true;

    let cancelled = false;

    (async () => {
      try {
        // Has this user already been notified about the current version?
        const { data: seen, error: seenErr } = await supabase
          .from('user_onboarding')
          .select('status')
          .eq('user_id', userId)
          .eq('tour_id', PRODUCT_UPDATE_TOUR_ID)
          .eq('version', CURRENT_PRODUCT_VERSION)
          .maybeSingle();

        if (cancelled) return;
        if (seenErr) throw seenErr;
        if (seen && TERMINAL_STATUSES.includes(seen.status)) return;

        // Emit the notification (idempotent per user+version server-side).
        const { error: rpcErr } = await supabase.rpc('notify_product_update', {
          p_version: CURRENT_PRODUCT_VERSION,
          p_title: PRODUCT_UPDATE_TITLE,
          p_body: PRODUCT_UPDATE_BODY,
          p_workspace_id: wsId,
        });
        if (cancelled) return;
        if (rpcErr) throw rpcErr;

        // Record last-seen so we don't re-emit on the next session.
        const { error: markErr } = await supabase
          .from('user_onboarding')
          .upsert(
            {
              user_id: userId,
              workspace_id: wsId,
              tour_id: PRODUCT_UPDATE_TOUR_ID,
              version: CURRENT_PRODUCT_VERSION,
              status: 'completed',
            },
            { onConflict: 'user_id,tour_id,version' },
          );
        if (markErr) throw markErr;
      } catch (err) {
        if (cancelled) return;
        captureError(err as Error, { feature: 'product-update-notification' });
        // Silent for the user: a failed "what's new" emit must not interrupt
        // the dashboard. We'll retry next session (checkedRef resets on remount).
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authLoading, userId, wsId]);
}
